import { createContext, useContext, useEffect, useState } from 'react';
import translations, { t as translate } from '../i18n/translations';

const TenantContext = createContext(null);

const DEFAULTS = {
  name:         'HelpDesk TI',
  short:        'HelpDesk',
  lang:         'es',
  primaryColor: '#0085CA',
  accentColor:  '#003D6B',
  logoUrl:      null,
  appName:      'HelpDesk TI',
  country:      'ES',
};

function applyColors(primary, accent) {
  function hexToRgb(hex) {
    const r = parseInt(hex.slice(1,3),16);
    const g = parseInt(hex.slice(3,5),16);
    const b = parseInt(hex.slice(5,7),16);
    return {r,g,b};
  }
  function lighten(hex, pct) {
    const {r,g,b} = hexToRgb(hex);
    const f = pct/100;
    return `rgb(${Math.round(r+(255-r)*f)},${Math.round(g+(255-g)*f)},${Math.round(b+(255-b)*f)})`;
  }
  function darken(hex, pct) {
    const {r,g,b} = hexToRgb(hex);
    const f = 1-pct/100;
    return `rgb(${Math.round(r*f)},${Math.round(g*f)},${Math.round(b*f)})`;
  }
  const root = document.documentElement;
  root.style.setProperty('--una-blue',     primary);
  root.style.setProperty('--una-blue-h',   darken(primary, 20));
  root.style.setProperty('--una-blue-lt',  lighten(primary, 90));
  root.style.setProperty('--fd-accent',    primary);
  root.style.setProperty('--fd-accent-h',  darken(primary, 20));
  root.style.setProperty('--fd-accent-lt', lighten(primary, 90));
  root.style.setProperty('--bd-accent',    primary);
  root.style.setProperty('--tenant-primary', primary);
  root.style.setProperty('--tenant-accent',  accent || primary);
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(DEFAULTS);
  const [ready,  setReady]  = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tenantOverride = params.get('_tenant');
    if (tenantOverride) sessionStorage.setItem('_tenant_preview', tenantOverride);
    const previewTenant = tenantOverride || sessionStorage.getItem('_tenant_preview') || '';

    // Preview token — permite al superadmin acceder a cualquier tenant con su propio token
    const previewToken = params.get('_preview_token');
    if (previewToken) {
      localStorage.setItem('hd_token', previewToken);
    }
    const url = previewTenant ? `/api/tenant/config?_tenant=${previewTenant}` : '/api/tenant/config';

    fetch(url)
      .then(r => r.json())
      .then(data => {
        const merged = { ...DEFAULTS, ...data, modules: data.modules ?? null };
        setTenant(merged);
        applyColors(merged.primaryColor, merged.accentColor);
        document.title = merged.appName || DEFAULTS.appName;
      })
      .catch(() => {
        applyColors(DEFAULTS.primaryColor, DEFAULTS.accentColor);
      })
      .finally(() => setReady(true));
  }, []);

  // t() helper bound to tenant language
  const tFn = (key) => translate(key, tenant.lang || 'es');

  return (
    <TenantContext.Provider value={{ ...tenant, t: tFn }}>
      {ready ? children : null}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}

// Hook conveniente: const { t } = useT()
export function useT() {
  const ctx = useContext(TenantContext);
  // Permitir override de idioma por usuario (selector en AppBar)
  const langOverride = localStorage.getItem('bd_lang_override');
  const lang = langOverride || ctx?.lang || 'es';
  const tFn = (key) => translate(key, lang);
  return { t: tFn, lang };
}
